/**
 * LES ÉCRITURES EN COURS ET LEUR ISSUE, CIBLE PAR CIBLE.
 * ============================================================================
 *
 * INTENTION : chaque geste dit vrai, et ne se rejoue jamais à l'aveugle.
 *  • Rien ne bascule avant la réponse : ce store ne touche à aucune donnée
 *    affichée. Il lance l'appel, attend la réponse, puis fait RELIRE l'écran
 *    (la surveillance, la liste, le résultat d'un test) par la fonction que
 *    l'écran lui donne.
 *  • Un double clic n'envoie qu'une requête : tant qu'une écriture est en
 *    vol sur une cible (une surveillance, une alarme, un destinataire), toute
 *    autre écriture sur cette cible est ignorée, sans appel.
 *  • Une écriture dont on ne sait pas si elle est passée (délai dépassé,
 *    réseau coupé, 5xx) reste « incertaine » : l'écran le dit, relit, et
 *    aucun geste sur la cible n'est repris avant une relecture réussie. Il
 *    en va de même d'un succès dont la relecture a échoué : l'écran montre
 *    encore l'état d'avant, et ni un geste ni l'ouverture d'un dialogue
 *    n'efface l'avertissement avant une relecture réussie.
 *  • Tenu par session (personne et entreprise) : une réponse arrivée pour une
 *    autre session est jetée, sans rien poser ni rien relire.
 *
 * Aucune donnée saisie n'y est gardée : le store ne reçoit que l'appel, déjà
 * fermé sur ses arguments (un numéro de téléphone n'y passe jamais en clair).
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import {
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import { estIncertaine } from "@modules/alarmes-v2/utils/ecritures"

/** Les gestes d'écriture du lot F6, tels que l'écran les nomme. */
export type GesteEcriture =
    | "acquitter"
    | "parametres"
    | "desactiver"
    | "reactiver"
    | "destinataires"
    | "inscrire"
    | "retirer"
    | "consigne"
    | "test"
    | "numero"
    | "avis"

/** Ce que l'écran a appris d'une écriture. */
export type IssueEcriture<T> =
    | { issue: "fait"; valeur: T }
    | { issue: "refus"; erreur: ErreurAlarmesV2 }
    | { issue: "incertain"; erreur: ErreurAlarmesV2 }
    /** Rien n'est parti : une écriture est en vol, ou une incertitude attend sa relecture. */
    | { issue: "ignoree" }
    /** La réponse est arrivée pour une autre session : jetée. */
    | { issue: "jetee" }

/** Où en est la relecture qui suit une réponse. */
export type StatutRelecture = "en_cours" | "faite" | "echouee"

/** L'état d'une cible : en vol, ou la dernière issue connue. */
export interface EtatCible {
    geste: GesteEcriture
    statut: "envoi" | "fait" | "refus" | "incertain"
    erreur: ErreurAlarmesV2 | null
    /** La relecture qui suit la réponse ; `null` pendant l'envoi. */
    relecture: StatutRelecture | null
    /** La valeur rendue par un succès, pour l'annonce (jamais une saisie). */
    valeur: unknown
}

/** Une écriture à lancer. */
export interface Ecriture<T> {
    /** La cible : `surveillance:<id>`, `alarme:<id>`, `test:<id>:<id>`… */
    cible: string
    geste: GesteEcriture
    /** L'appel, UNE requête. */
    appel: () => Promise<T>
    /**
     * Relit ce que l'écran montre de la cible ; rend vrai si la relecture a
     * réussi. Appelée après toute réponse de la session courante.
     */
    relire: () => Promise<boolean>
}

export const useActionsStore = defineStore("alarmesV2Actions", () => {
    /** L'état de chaque cible, pour la session lue. */
    const cibles = ref<Record<string, EtatCible>>({})

    /** La session à laquelle `cibles` appartient. */
    let sessionLue = ""

    /** Vide tout si la session a changé. */
    function suivreLaSession(): string {
        const session = cleDeSession()
        if (session !== sessionLue) {
            cibles.value = {}
            sessionLue = session
        }
        return session
    }

    function poser(cible: string, etat: EtatCible): void {
        cibles.value = { ...cibles.value, [cible]: etat }
    }

    /**
     * L'état d'une cible pour la session courante, ou `null`.
     * @param cible - la cible
     */
    function etat(cible: string): EtatCible | null {
        const lu = cibles.value[cible] ?? null
        return cleDeSession() === sessionLue ? lu : null
    }

    /**
     * Vrai quand aucun geste ne doit partir sur la cible : une écriture en
     * vol, une relecture en cours (l'écran n'est pas encore à jour), ou une
     * écriture passée (succès) ou peut-être passée (incertitude) dont la
     * relecture n'a pas encore réussi : l'écran montre l'état d'AVANT, et un
     * geste parti de lui écraserait ce qui vient d'être enregistré. Un refus
     * n'a rien écrit : il ne bloque pas.
     * @param cible - la cible
     */
    function occupee(cible: string): boolean {
        const e = etat(cible)
        if (!e) return false
        if (e.statut === "envoi" || e.relecture === "en_cours") return true
        return (
            (e.statut === "incertain" || e.statut === "fait") &&
            e.relecture !== "faite"
        )
    }

    /** Relit la cible et range l'issue de la relecture. */
    async function lancerLaRelecture(
        session: string,
        cible: string,
        relire: () => Promise<boolean>
    ): Promise<void> {
        const avant = cibles.value[cible]
        if (!avant) return
        poser(cible, { ...avant, relecture: "en_cours" })
        let reussie = false
        try {
            reussie = await relire()
        } catch {
            reussie = false
        }
        if (cleDeSession() !== session) return
        const courant = cibles.value[cible]
        if (!courant || courant.geste !== avant.geste) return
        poser(cible, { ...courant, relecture: reussie ? "faite" : "echouee" })
    }

    /**
     * Lance une écriture, puis la relecture. Ne rejette jamais.
     * @param e - la cible, le geste, l'appel et la relecture
     */
    async function ecrire<T>(e: Ecriture<T>): Promise<IssueEcriture<T>> {
        const session = suivreLaSession()
        if (!session || occupee(e.cible)) return { issue: "ignoree" }
        poser(e.cible, {
            geste: e.geste,
            statut: "envoi",
            erreur: null,
            relecture: null,
            valeur: null,
        })
        let issue: IssueEcriture<T>
        try {
            const valeur = await e.appel()
            issue = { issue: "fait", valeur }
        } catch (brut) {
            const erreur =
                brut instanceof ErreurAlarmesV2
                    ? brut
                    : versErreurAlarmesV2(brut)
            issue = estIncertaine(erreur)
                ? { issue: "incertain", erreur }
                : { issue: "refus", erreur }
        }
        // Une autre session : la réponse ne se montre pas, rien n'est relu.
        if (cleDeSession() !== session) return { issue: "jetee" }
        poser(e.cible, {
            geste: e.geste,
            statut: issue.issue as EtatCible["statut"],
            erreur: "erreur" in issue ? issue.erreur : null,
            relecture: null,
            valeur: issue.issue === "fait" ? issue.valeur : null,
        })
        await lancerLaRelecture(session, e.cible, e.relire)
        return issue
    }

    /**
     * Relance la relecture d'une cible incertaine dont la relecture a
     * échoué : un geste de la personne (« Relire »), jamais l'écriture.
     * @param cible - la cible
     * @param relire - la relecture de l'écran
     */
    async function relireLaCible(
        cible: string,
        relire: () => Promise<boolean>
    ): Promise<void> {
        const session = suivreLaSession()
        const e = etat(cible)
        if (
            !session ||
            !e ||
            e.statut === "envoi" ||
            e.relecture === "en_cours"
        )
            return
        await lancerLaRelecture(session, cible, relire)
    }

    /**
     * Oublie l'issue d'une cible (la personne ferme l'annonce, ou rouvre un
     * dialogue) ; jamais pendant un envoi ni avant la relecture d'une
     * incertitude.
     * @param cible - la cible
     */
    function oublier(cible: string): void {
        if (occupee(cible)) return
        const { [cible]: _oubliee, ...reste } = cibles.value
        cibles.value = reste
    }

    /** Remet le store à son état de départ. */
    function reinitialiser(): void {
        cibles.value = {}
        sessionLue = ""
    }

    return {
        cibles,
        etat,
        occupee,
        ecrire,
        relireLaCible,
        oublier,
        reinitialiser,
    }
})

/** La cible d'une surveillance (route 5). */
export const cibleSurveillance = (id: string) => `surveillance:${id}`
/** La cible d'une alarme à acquitter (route 7). */
export const cibleAlarme = (id: string) => `alarme:${id}`
/** La cible de l'avis sur une alarme (route 18). */
export const cibleAvis = (id: string) => `avis:${id}`
/** La cible d'un message de test à un destinataire (route 6). */
export const cibleTest = (idInstance: string, idUser: string) =>
    `test:${idInstance}:${idUser}`
/** La cible du numéro d'un membre (route 8). */
export const cibleNumero = (idUser: string) => `numero:${idUser}`
